// OnboardingTasks.tsx
// Drop‑in refactor: "Start Onboarding" button -> template picker -> seed checklist tasks
// - Keeps your existing AWS hooks (useBatchPostChecklists, useAddChecklistTask, useRemoveChecklistTask)
// - Lets users preview/select which tasks from a template to create
// - After seeding, shows a minimal table where tasks can be removed
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useAddChecklistTask } from 'api/aws/checklists/useBatchAddChecklistTask'
import { useBatchPostChecklists } from 'api/aws/checklists/useBatchPostChecklists'
import { useBatchUpdateChecklists } from 'api/aws/checklists/useBatchUpdateChecklists'
import { useRemoveChecklistTask } from 'api/aws/checklists/useRemoveCheclistTask'
import { useDeleteItems } from 'api/aws/organizations_table/useDeleteItems'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useUpdateFields } from 'api/aws/organizations_table/useUpdateFields'
import { useUserItems } from 'api/aws/users/useUserItems'
import { SEARCH_DATA_KEY, useEntitiesFromSearch } from 'api/search'
import DrawerTasksToolbar from 'components/DataGrid/components/DrawerTasksToolbar'
import Channels from 'components/NCO/Channels'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useAvailableHeight } from 'context/Settings/useAvaliableHeight'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { BoxScroller } from 'styles/elements/styleElements'
import { User } from 'types'
import { canonRole } from 'utils/role-utils'
import DeleteConfirmationDialog from './DeleteConfirmationDIalog'
import { DraftRows } from './DraftRows'
import NcoHoverMenu from './NcoHoverMenu'
import OnboardingTemplateSelector from './OnboardingTemplateSelector'
import SeededRows from './SeededRows'
import { createTaskTemplates } from './TASK_TEMPLATES'
import { useNcoHelpers } from './useNcoHelpers'

// === Types-ish ===
type OrgItem = any // OrganizationTableItem

type Props = {
  deal: OrgItem
  organization: OrgItem
  checklists: any[]
  checklistType?: string // defaults to 'Onboarding'
  checklistProgress: {
    gtm: number
    ga4: number
    fbpage: number
    completeseoform: number
  }
  expanded: boolean
  context?: 'nco' | 'projects' | string
  onChange: (fieldName: any, newValue: any) => Promise<void>
  currentChecklistOverride?: any | null
  seededRowsOverride?: any[] | null
  completedSetOverride?: Set<string> | null
  progressOverride?: {
    total: number
    done: number
    pct: number
    byRole: Record<string, { done: number; total: number }>
  } | null
}
const _STATUS_OPTIONS = ['Not Started', 'In Progress', 'Done'] as const
const _getTaskStatus = (task, completedTasks) => {
  let status = task.status

  if (status === 'Done') {
    completedTasks.add(task.id)
  }

  return status
}
const OnboardingTasks = ({
  deal,
  organization,
  checklists,
  checklistType = 'Onboarding',
  checklistProgress,
  expanded,
  onChange,
  currentChecklistOverride,
  seededRowsOverride,
  completedSetOverride,
  progressOverride,
}: Props) => {
  const { SK } = useParams()
  const { pinned } = useLayoutContext()
  // refs to measure
  const headerRef = useRef<HTMLDivElement | null>(null)

  // If you can grab a ref to the global toolbar, pass it in.
  // If not, skip toolbarRef and subtract a fixed height via `extraGutters` when pinned (see Option B note below).
  // const toolbarRef = useLayoutContext().toolbarRef
  // If you don't have toolbarRef, pick your known toolbar height (e.g., 64)
  const TOOLBAR_FALLBACK_H = 64
  const HEADER_H = 50 // global header bar
  const TOOLBAR_H = 64 // pinned global toolbar (only subtracted when pinned)
  const DRAWER_CONTENT_H = expanded ? 270 : 140 // Channels + Onboarding Team + progress bar + table header + margins

  // Calculate available height for the scrollable table area
  const maxHeight = `calc(100dvh - ${HEADER_H}px - ${pinned ? TOOLBAR_H : 0}px - ${DRAWER_CONTENT_H}px)`
  const { heightCss } = useAvailableHeight({
    headerRef,
    // toolbarRef, // if you have it
    toolbarPinned: pinned,
    extraGutters: pinned ? TOOLBAR_FALLBACK_H : 0, // remove if you pass toolbarRef
    min: 240,
  })
  const { products } = useEntitiesFromSearch()
  const relatedProducts = useMemo(() => {
    if (!products || !products?.length) return []
    return products?.filter(p => p?.deal_uuid === deal?.deal_uuid)
  }, [products])
  const TASK_TEMPLATES = createTaskTemplates(deal) ?? []
  const { mutateAsync: updateFields } = useUpdateFields()
  const { syncDealsCache, syncDealFields } = useSyncQueryCache()
  const qc = useQueryClient()
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
  const { mutate: batchUpdate } = useBatchUpdateChecklists()
  const { mutate: postChecklists, isPending: creatingChecklist } = useBatchPostChecklists()
  const { mutate: addChecklistTask, isPending: addingTasks } = useAddChecklistTask()
  const { mutate: removeChecklistTask } = useRemoveChecklistTask()
  const { mutate: deleteItems, isPending: deletingChecklist } = useDeleteItems()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const orgPK = organization?.PK
  const orgUUID = organization?.org_uuid
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [isHovered, setIsHovered] = useState<boolean>(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState(TASK_TEMPLATES[0].id)
  const [newRowCounter, setNewRowCounter] = useState(0)
  const rawTemplate = useMemo(() => {
    if (Array.isArray(TASK_TEMPLATES)) {
      return TASK_TEMPLATES.find(t => t.id === selectedTemplateId) || null
    }
    // if TASK_TEMPLATES is a map/object
    return TASK_TEMPLATES?.[selectedTemplateId] || null
  }, [selectedTemplateId])

  const selectedTemplate = useMemo(() => {
    if (!rawTemplate) return { items: [], sections: [] }
    return {
      ...rawTemplate,
      items: Array.isArray(rawTemplate.items) ? rawTemplate.items : [],
      sections: Array.isArray((rawTemplate as any).sections) ? (rawTemplate as any).sections : [],
    }
  }, [rawTemplate])
  const searchTeam = useMemo(() => (users ?? []).filter(u => (deal?.onboardingTeamIds ?? []).includes(u?.user_uuid)), [
    users,
    deal?.onboardingTeamIds,
  ])
  // Memoize currentTeam to prevent downstream re-renders
  const currentTeam = useMemo(
    () =>
      searchTeam?.map(u => {
        if (u?.role === 'Integration') return { ...u, role: 'MIA' }
        return u
      }) ?? [],
    [searchTeam]
  )
  const PM_USERS = useMemo(() => {
    if (!currentTeam || !currentTeam?.length) return null
    return currentTeam?.filter(t => t?.role === 'PM') ?? null
  }, [currentTeam])
  const DEAL_OWNER = useMemo(() => {
    if (!deal || !users) return null
    const OWNER_ID = (deal?.owner?.[0] || deal?.owner) ?? null
    return OWNER_ID ? users?.find(u => u?.user_uuid === OWNER_ID) : null
  }, [deal])
  const MIA_USERS = useMemo(() => {
    if (!currentTeam || !currentTeam?.length) return null
    return currentTeam?.filter(t => t?.role === 'MIA' || t?.role === 'Integration' || t?.role === 'DEV') ?? null
  }, [currentTeam])
  const IS_OWNER = (currentUser && currentUser?.user_uuid === DEAL_OWNER?.user_uuid) ?? false
  // const IS_PM = (currentUser && PM_USERS?.some(u => u?.user_uuid === currentUser?.user_uuid)) ?? false
  const IS_PM = (currentUser && (currentUser?.role === 'PM' || currentUser?.role === 'DEV')) ?? false
  const IS_MIA = (currentUser && MIA_USERS?.some(u => u?.user_uuid === currentUser?.user_uuid)) ?? false

  // ids by canon role (recomputes whenever team changes)
  const idsByRole = useMemo(() => {
    const m = new Map<string, string[]>()
    ;(currentTeam ?? []).forEach(u => {
      const k = canonRole(u.role ?? (u as any).slackRole)
      if (!k) return
      if (!m.has(k)) m.set(k, [])
      m.get(k)!.push(u.user_uuid)
    })
    return m
  }, [currentTeam])

  // Checkbox selection state for the chosen template
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  useEffect(() => {
    setSelectedKeys(selectedTemplate.items.map(i => i.key))
  }, [selectedTemplate])

  const [boundChecklistIds, setBoundChecklistIds] = useState<{ PK: string; SK: string } | null>(null)
  const listKey = ['checklists', deal?.PK] as const
  const currentChecklist = useMemo(() => {
    if (currentChecklistOverride) return currentChecklistOverride
    return (
      (checklists || []).find(
        (ch: any) =>
          (ch?.PK === orgPK || ch?.orgPK === orgPK || ch?.PK === orgPK) &&
          (ch?.type === checklistType || ch?.type === 'General')
      ) || null
    )
  }, [currentChecklistOverride, checklists, orgPK, checklistType])
  const activeIds =
    boundChecklistIds ?? (currentChecklist ? { PK: currentChecklist.PK, SK: currentChecklist.SK } : null)
  const {
    genTempId,
    addItemToCache,
    handleAddRow,
    saveDraftRow,
    draftRows,
    patchDraftRow,
    discardDraftRow,
    invalidateOnlyChecklists,
  } = useNcoHelpers(deal, activeIds, currentChecklist)
  useEffect(() => {
    if (currentChecklist) {
      setBoundChecklistIds({ PK: currentChecklist.PK, SK: currentChecklist.SK })
    }
  }, [currentChecklist])

  const handleStartClick = () => setPickerOpen(true)
  // helper: build item objects for sections[0].items

  const buildItemsFromTemplate = (items: any[], baseIndex = 0) =>
    items.map((t, idx) => ({
      id: t.key, // server can store as id; keep itemId if your API expects it
      itemId: t.key,
      key: t.key,
      title: t.name,
      name: t.name,
      role: t.role ?? '',
      status: 'Not Started',
      // dependencies: t.dependencies ?? [],
      dependencies: [],
      itemIndex: baseIndex + idx,
      assigneeIds: idsByRole.get(canonRole(t.role)) ?? [],
    }))

  // helper: build a full checklist doc with items in sections[0]
  const buildChecklistDoc = ({
    PK,
    SK,
    type,
    title,
    items,
  }: {
    PK: string
    SK: string
    type: string
    title: string
    items: any[]
  }) => ({
    PK,
    SK,
    type,
    title,
    sections: [{ sectionIndex: 0, title, items }],
    createdAt: new Date().toISOString(),
  })

  // IMPORTANT: give each template its own checklist identity
  const templateType = (tplId: string) => {
    switch (tplId) {
      case 'onboarding':
        return 'Onboarding'
      case 'premiumWebsite':
        return 'WebsitePremium'
      case 'newWebsite':
        return 'WebsiteNew'
      default:
        return checklistType || 'Onboarding'
    }
  }
  const templateTitle = (tplId: string) => {
    switch (tplId) {
      case 'onboarding':
        return 'Marketing Onboarding'
      case 'premiumWebsite':
        return 'Website – Premium'
      case 'newWebsite':
        return 'Website – New'
      default:
        return checklistType || 'Onboarding'
    }
  }
  // All candidate users (current onboarding team only)
  const onboardingTeam: User[] = useMemo(() => {
    const ids = Array.isArray(deal?.onboardingTeamIds) ? deal.onboardingTeamIds : []
    if (!ids.length || !users) return []
    const byId = new Map(users.map(u => [u.user_uuid, u]))
    return ids.map(id => byId.get(id)).filter(Boolean) as User[]
  }, [deal?.onboardingTeamIds, users])
  // ID -> display name
  const nameById = useMemo(() => {
    const m = new Map<string, string>()
    onboardingTeam.forEach((u: any) => {
      const id = u.user_uuid || u.id
      const nm = u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || id
      if (id) m.set(id, nm)
    })
    return m
  }, [onboardingTeam])

  const handleConfirmSeed = async () => {
    const chosen = selectedTemplate?.items?.filter(i => selectedKeys.includes(i.key)) ?? []
    if (!chosen.length) return setPickerOpen(false)
    // prune by products
    const hasSearch = relatedProducts?.some(
      (p: any) =>
        (p?.category || p?.category_name || '').toLowerCase().includes('digital') &&
        (p?.subcategory || '').toLowerCase().includes('search')
    )
    const hasSocial = relatedProducts?.some(
      (p: any) =>
        (p?.category || p?.category_name || '').toLowerCase().includes('digital') &&
        (p?.subcategory || '').toLowerCase().includes('social')
    )
    const filtered = chosen.filter(t => {
      if (t.key === 'searchCampaignCreation') return hasSearch
      if (t.key === 'createSocialDisplayCampaigns') return hasSocial
      return true
    })
    if (!filtered.length) return setPickerOpen(false)

    const PK = deal?.PK
    const SKnew = `CHECKLIST_${templateType(selectedTemplateId)}_${organization?.org_uuid ?? 'unknown'}`
    const candidateKeys: readonly any[][] = [
      ['checklists', deal?.PK],
      ['checklists', deal?.SK],
      ['checklists', organization?.PK],
      ['checklists'], // catch-all
    ]

    // utility: append/replace the checklist in an array
    const upsertChecklist = (arr: any[], doc: any) => {
      const idx = (arr ?? []).findIndex(c => c?.PK === doc.PK && c?.SK === doc.SK)
      if (idx === -1) return [...(arr ?? []), doc]
      const next = [...arr]
      next[idx] = doc
      return next
    }

    // helper to build new items (you already have)
    const baseIndexFrom = (checklistDoc: any) => checklistDoc?.sections?.[0]?.items?.length ?? 0

    // snapshot touched keys for rollback
    const touched: { key: any[]; prev: any }[] = []

    const flushOptimistic = (producer: (prev: any[]) => any[]) => {
      candidateKeys.forEach(key => {
        const prev = qc.getQueryData<any[]>(key as any)
        if (prev !== undefined) {
          touched.push({ key, prev })
          qc.setQueryData(key as any, producer(prev))
        }
      })
    }

    // check if exists in the primary key
    const primaryKey = ['checklists', PK] as const
    const prevLists = qc.getQueryData<any[]>(primaryKey) || []
    const existing = prevLists.find(c => c.PK === PK && c.SK === SKnew)

    if (!existing) {
      // CREATE brand-new checklist (full doc)
      const items = buildItemsFromTemplate(filtered, 0)
      const fullDoc = buildChecklistDoc({
        PK,
        SK: SKnew,
        type: templateType(selectedTemplateId),
        title: templateTitle(selectedTemplateId),
        items,
      })

      // cancel outstanding checklist queries
      await Promise.all(candidateKeys.map(key => qc.cancelQueries({ queryKey: key, exact: false })))

      // optimistic upsert across all keys
      flushOptimistic(prev => upsertChecklist(prev ?? [], fullDoc))
      setBoundChecklistIds({ PK, SK: SKnew })
      setPickerOpen(false)

      // persist
      postChecklists([fullDoc], {
        onError: e => {
          console.error('❌ postChecklists error', e)
          // rollback all touched keys
          touched.forEach(({ key, prev }) => qc.setQueryData(key as any, prev))
          setBoundChecklistIds(null)
        },
        onSuccess: async () => {
          await updateFields({
            updates: [
              {
                tableName: 'ORGANIZATIONS_TABLE',
                PK: deal.PK,
                SK: deal.SK,
                fieldName: 'onboardingStatus',
                newValue: 'Awaiting Kickoff',
              },
            ],
          })
          syncDealFields(qc, { ...deal, onboardingStatus: 'Awaiting Kickoff' }, [
            { fieldName: 'onboardingStatus', newValue: 'Awaiting Kickoff' },
          ])
          syncDealsCache(qc, { ...deal, onboardingStatus: 'Awaiting Kickoff' })
        },
        onSettled: () => {
          invalidateOnlyChecklists()
          // refetch ALL checklist variants
          // qc.invalidateQueries({
          //   predicate: q => Array.isArray(q.queryKey) && q.queryKey[0] === 'checklists',
          // })
        },
      })
    } else {
      // APPEND to existing checklist (de-dup) across all keys
      const existingIds = new Set((existing.sections?.[0]?.items ?? []).map((it: any) => it.id ?? it.itemId ?? it.key))
      const toAdd = filtered.filter(t => !existingIds.has(t.key))
      if (!toAdd.length) {
        setPickerOpen(false)
        return
      }

      const baseIndex = baseIndexFrom(existing)
      const newItems = buildItemsFromTemplate(toAdd, baseIndex)

      // function to produce the appended doc from a prev array
      const appendProducer = (prev: any[]) => {
        const list = prev ?? []
        const doc = list.find(c => c?.PK === PK && c?.SK === SKnew)
        const target = doc ?? {
          PK,
          SK: SKnew,
          type: templateType(selectedTemplateId),
          title: templateTitle(selectedTemplateId),
          sections: [{ sectionIndex: 0, title: templateTitle(selectedTemplateId), items: [] }],
        }
        const sections = target.sections?.length
          ? target.sections
          : [{ sectionIndex: 0, title: target.title, items: [] }]
        const first = sections[0]
        const nextFirst = { ...first, items: [...(first.items ?? []), ...newItems] }
        const nextDoc = { ...target, sections: [nextFirst, ...sections.slice(1)] }
        return upsertChecklist(list, nextDoc)
      }

      // cancel outstanding checklist queries
      await Promise.all(candidateKeys.map(key => qc.cancelQueries({ queryKey: key, exact: false })))

      // optimistic append across all keys
      flushOptimistic(appendProducer)
      setPickerOpen(false)

      // persist the items
      addChecklistTask(
        newItems.map((it: any) => ({
          PK,
          SK: SKnew,
          sectionIndex: 0,
          itemId: it.itemId,
          key: it.key,
          name: it.name,
          role: it.role,
          status: it.status,
          // dependencies: it.dependencies,
          dependencies: [],
          itemIndex: it.itemIndex,
          assigneeIds:
            currentTeam?.map(team => (team?.role === it.role ? team?.user_uuid : null))?.filter(Boolean) ?? [],
        })),
        {
          onError: e => {
            touched.forEach(({ key, prev }) => qc.setQueryData(key as any, prev))
          },
          onSettled: () => {
            invalidateOnlyChecklists()
          },
        }
      )
    }
  }

  const handleRemoveSeeded = (row: any) => {
    if (!boundChecklistIds) return
    const { PK, SK } = boundChecklistIds
    const sectionIndex = row.sectionIndex ?? 0
    const itemId = row.itemId

    // any keys your app might use for checklists
    const candidateKeys: readonly any[][] = [
      ['checklists', deal?.PK],
      // ['checklists', deal?.SK],
      // ['checklists', organization?.PK],
      // ['checklists'], // catch-all list
    ]

    const stripItemFromChecklist = (arr: any[]) => {
      return (arr ?? []).map(ch => {
        if (ch.PK !== PK || ch.SK !== SK) return ch

        const sections = ch.sections || []
        const sIdx = sections.findIndex((s: any) => (s.sectionIndex ?? 0) === sectionIndex)
        if (sIdx === -1) return ch

        const section = sections[sIdx]
        // 1) remove the target item
        const remaining = (section.items || []).filter((it: any) => (it.id ?? it.itemId ?? it.key) !== itemId)
        // 2) remove dangling dependencies that referenced the deleted item
        const cleaned = remaining.map((it: any) => ({
          ...it,
          // dependencies: Array.isArray(it.dependencies) ? it.dependencies.filter((d: string) => d !== itemId) : [],
          dependencies: [],
        }))
        // 3) optionally reindex itemIndex to keep order nice
        const reindexed = cleaned
          .sort((a: any, b: any) => (a.itemIndex ?? 0) - (b.itemIndex ?? 0))
          .map((it: any, idx: number) => ({ ...it, itemIndex: idx }))

        const nextSections = [...sections]
        nextSections[sIdx] = { ...section, items: reindexed }
        return { ...ch, sections: nextSections }
      })
    }

    // cancel queries for keys we’ll touch
    Promise.all(candidateKeys.map(key => qc.cancelQueries({ queryKey: key, exact: false }))).then(() => {
      // snapshot + optimistic update for every key that exists
      const touched: { key: any[]; prev: any }[] = []
      candidateKeys.forEach(key => {
        const prev = qc.getQueryData<any[]>(key as any)
        if (prev !== undefined) {
          touched.push({ key, prev })
          qc.setQueryData(key as any, stripItemFromChecklist(prev))
        }
      })

      // persist delete to backend
      removeChecklistTask(
        { PK, SK, sectionIndex, itemId, itemIndex: row.orderIndex ?? 0 },
        {
          onError: () => {
            // rollback everything we touched
            touched.forEach(({ key, prev }) => qc.setQueryData(key as any, prev))
          },
          onSettled: () => {
            // optionally, only refetch the affected list keys (not the whole app)
            // qc.invalidateQueries({ queryKey: ['checklists', deal?.PK] })
            // If you also surface this doc under other keys, invalidate them too:
            // qc.invalidateQueries({ queryKey: ['checklists', deal?.SK] })
            // qc.invalidateQueries({ queryKey: ['checklists', organization?.PK] })
          },
        }
      )
    })
  }

  const handleDeleteEntireList = () => {
    if (!currentChecklist) return

    const payload = {
      EntityType: 'checklist',
      tableName: 'CHECKLIST_TABLE',
      items: [{ PK: currentChecklist.PK, SK: currentChecklist.SK }],
    }

    // any keys your app might use for checklists
    const candidateKeys: readonly any[][] = [
      ['checklists', deal?.PK],
      ['checklists', deal?.SK],
      ['checklists', organization?.PK],
      ['checklists'], // catch-all list
    ]

    const stripChecklist = (arr: any[], target: any) =>
      (arr ?? []).filter(c => !(c?.PK === target.PK && c?.SK === target.SK))

    deleteItems(payload, {
      // onMutate: async () => {
      //   // cancel all relevant queries first
      //   await Promise.all(candidateKeys.map(key => qc.cancelQueries({ queryKey: key, exact: false })))

      //   // snapshot + optimistic update for every key that exists
      //   const touched: { key: any[]; prev: any }[] = []
      //   candidateKeys.forEach(key => {
      //     const prev = qc.getQueryData<any[]>(key as any)
      //     if (prev !== undefined) {
      //       touched.push({ key, prev })
      //       qc.setQueryData(key as any, stripChecklist(prev, currentChecklist))
      //     }
      //   })

      //   // clear local binding so the table empties immediately
      //   setBoundChecklistIds(null)

      //   return { touched }
      // },
      onError: (_err, _vars, ctx: any) => {
        // rollback everything we touched
        ctx?.touched?.forEach(({ key, prev }: any) => qc.setQueryData(key as any, prev))
      },
      onSettled: () => {
        // nuke any detail cache for this one checklist (if you keep one)
        qc.removeQueries({
          queryKey: ['checklist', currentChecklist.PK],
          exact: true,
        })
        // refetch EVERY variant of "checklists" after mutation settles
        qc.invalidateQueries({
          predicate: q => Array.isArray(q.queryKey) && q.queryKey[0] === 'checklists',
        })
        setConfirmOpen(false)
      },
    })
  }

  async function persistProgressFromCache() {
    // Read the latest doc from the cache and recompute
    const arr = qc.getQueryData<any[]>(listKey) || []
    const doc = (arr || []).find((ch: any) => activeIds && ch.PK === activeIds.PK && ch.SK === activeIds.SK)
    const items = doc?.sections?.[0]?.items || []
    const total = items.length
    const done = items.filter(it => it.status === 'Done').length
    const pct = total ? Math.round((done / total) * 100) : 0
    const newValue = { total, done, pct }

    await updateFields({
      updates: [
        {
          tableName: 'ORGANIZATIONS_TABLE',
          PK: deal.PK,
          SK: deal.SK,
          fieldName: 'onboardingProgress',
          newValue,
        },
        {
          tableName: 'ORGANIZATIONS_TABLE',
          PK: deal.PK,
          SK: deal.SK,
          fieldName: 'progress',
          newValue,
        },
      ],
    })
    syncDealFields(qc, { ...deal, onboardingProgress: newValue }, [
      { fieldName: 'onboardingProgress', newValue },
      { fieldName: 'progress', newValue },
    ])
    syncDealsCache(qc, { ...deal, onboardingProgress: newValue, progress: newValue })
  }
  // ⚠️ DELETE your existing 'const seededRows = useMemo(...)' + completedSet + progress

  // Local compute (fallback) for rows:
  const localSeededRows = useMemo(() => {
    if (!currentChecklist) return []
    const rows =
      currentChecklist.sections?.flatMap((section: any) =>
        (section.items || []).map((it: any, idx: number) => ({
          orderIndex: it.itemIndex ?? idx,
          name: it.title ?? it.name,
          role: it.role ?? '',
          status: it.status ?? 'Not Started',
          sectionIndex: section.sectionIndex ?? 0,
          itemId: it.id ?? it.itemId ?? it.key,
          assigneeIds: it.assigneeIds ?? [],
          // dependencies: Array.isArray(it.dependencies) ? it.dependencies : [],
          dependencies: [],
        }))
      ) || []

    rows.sort((a: any, b: any) => a.orderIndex - b.orderIndex)
    const statusById = new Map(rows.map(r => [r.itemId, r.status]))
    return rows.map(r => ({
      ...r,
      canEdit: !r.dependencies?.length || r.dependencies.every((dep: string) => statusById.get(dep) === 'Done'),
    }))
  }, [currentChecklist])

  // 👉 Use override if provided, otherwise fallback
  const seededRows = seededRowsOverride ?? localSeededRows

  // Local compute (fallback) for completed set:
  const localCompletedSet = useMemo(() => new Set(seededRows.filter(r => r.status === 'Done').map(r => r.itemId)), [
    seededRows,
  ])
  // 👉 Use override if provided
  const completedSet = completedSetOverride ?? localCompletedSet

  // Local compute (fallback) for progress summary:
  const localProgress = useMemo(() => {
    const total = seededRows.length
    const done = seededRows.filter(r => r.status === 'Done').length
    const pct = total ? Math.round((done / total) * 100) : 0
    const byRole = seededRows.reduce((acc: any, r: any) => {
      const key = r.role || '—'
      if (!acc[key]) acc[key] = { done: 0, total: 0 }
      acc[key].total += 1
      if (r.status === 'Done') acc[key].done += 1
      return acc
    }, {})
    return { total, done, pct, byRole }
  }, [seededRows])
  // 👉 Use override if provided
  const progress = progressOverride ?? localProgress

  ////////////////////////////////////////// UPDATES ///////////////////////////////////////////
  function patchItemInCache({ itemId, sectionIndex, patch }: { itemId: string; sectionIndex: number; patch: any }) {
    const prev = qc.getQueryData<any[]>(listKey) || []
    const next = prev.map(ch => {
      if (!activeIds || ch.PK !== activeIds.PK || ch.SK !== activeIds.SK) return ch
      const sections = ch.sections || []
      const sIdx = sections.findIndex((s: any) => (s.sectionIndex ?? 0) === (sectionIndex ?? 0))
      if (sIdx === -1) return ch
      const section = sections[sIdx]
      const items = (section.items || []).map((it: any) => {
        if ((it.id ?? it.itemId ?? it.key) !== itemId) return it
        // careful merge for arrays (esp. assigneeIds)
        const merged: any = { ...it, ...patch }
        if (patch?.assigneeIds) {
          const cur = Array.isArray(it.assigneeIds) ? it.assigneeIds : []
          const add = patch.assigneeIds
          merged.assigneeIds = Array.from(new Set([...cur, ...add]))
        }
        return merged
      })
      const newSections = [...sections]
      newSections[sIdx] = { ...section, items }
      return { ...ch, sections: newSections }
    })
    qc.setQueryData(listKey, next)
    return { key: listKey, prev }
  }

  function updateStatus(row: any, newStatus: string) {
    if (!activeIds) return
    const ctx = patchItemInCache({
      itemId: row.itemId,
      sectionIndex: row.sectionIndex,
      patch: { status: newStatus },
    })

    // persist
    batchUpdate(
      [
        {
          PK: activeIds.PK,
          SK: activeIds.SK,
          itemId: row.itemId,
          fieldName: 'status',
          newStatus, // matches your existing backend payload
          sectionIndex: row.sectionIndex ?? 0,
          itemIndex: row.orderIndex ?? 0,
        },
      ],
      {
        onError: () => qc.setQueryData(ctx.key, ctx.prev), // rollback
        onSettled: () => {
          persistProgressFromCache()
        },
      }
    )
  }
  function updateAssignees(row: any, newIds: string[]) {
    if (!activeIds) return
    const sectionIndex = row.sectionIndex ?? 0
    const itemIndex = row.orderIndex ?? 0

    // optimistic cache patch
    const ctx = patchItemInCache({
      itemId: row.itemId,
      sectionIndex,
      patch: { assigneeIds: newIds },
    })

    // persist
    batchUpdate(
      [
        {
          PK: activeIds.PK,
          SK: activeIds.SK,
          itemId: row.itemId,
          fieldName: 'assigneeIds',
          newValue: newIds, // <- your API expects newValue for non-status fields
          sectionIndex,
          itemIndex,
        },
      ],
      {
        onError: () => qc.setQueryData(ctx.key, ctx.prev), // rollback
      }
    )
  }
  // OnboardingTasks.tsx

  const updateOnChange = async (fieldName: string, newValue: any) => {
    if (!deal) return
    const allKey = SEARCH_DATA_KEY
    const prevAll = qc.getQueryData<any>(allKey)

    // optimistic: update allItems cache (minimal touch)
    qc.setQueryData(allKey, (all: any) => {
      if (!all) return all
      const deals = (all.deals ?? []).map((d: any) => (d.SK === deal.SK ? { ...d, [fieldName]: newValue } : d))
      return { ...all, deals }
    })
    const fieldsToUpdate = [{ fieldName, newValue }]
    const updates = fieldsToUpdate.map(({ fieldName, newValue }) => ({
      tableName: 'ORGANIZATIONS_TABLE',
      PK: deal.PK,
      SK: deal.SK,
      fieldName,
      newValue,
    }))
    try {
      await updateFields({ updates })

      // keep your existing helper to sync derived caches/indexes
      syncDealFields(qc, { ...deal, [fieldName]: newValue }, [{ fieldName, newValue }])
      syncDealsCache(qc, { ...deal, [fieldName]: newValue })
    } catch (e) {
      // rollback on error
      qc.setQueryData(allKey, prevAll)
      throw e
    }
  }

  // Pre-compute team by role for O(1) lookups instead of O(n) filter on every row
  const teamByRole = useMemo(() => {
    const map = new Map<string, any[]>()
    ;(currentTeam ?? []).forEach(u => {
      const role = canonRole(u.role ?? (u as any).slackRole)
      if (!role) return
      if (!map.has(role)) map.set(role, [])
      map.get(role)!.push(u)
    })
    return map
  }, [currentTeam])

  // utility: team options, filtered by row.role if present
  const teamForRow = useCallback((row: any) => (row.role ? teamByRole.get(canonRole(row.role)) ?? [] : currentTeam), [
    teamByRole,
    currentTeam,
  ])

  /////////////////////////////////////////////////////////////////////////////////////////////
  // const expandedHeight = expanded ? 'calc(100dvh - 390px)' : 'calc(100dvh - 250px)'
  // const pinnedHeight = pinned ? `calc(${calcHeight}px - 660px)` : calcHeight
  // useEffect(() => {
  //   if (currentChecklist) {
  //     console.log('currentChecklist', currentChecklist)
  //   }
  // }, [currentChecklist])
  return (
    <Box>
      <Channels
        deal={deal}
        handleChange={(fieldName: string, newValue: any) => updateOnChange(fieldName, newValue)}
        checklistProgress={checklistProgress}
      />
      <Box
        className="drawer-toolbar"
        sx={{
          position: 'relative',
          mb: 1,
          // show the actions when the header (this Box) is hovered or focused
          '&:hover .toolbar-actions, &:focus-within .toolbar-actions': {
            opacity: 1,
            transform: 'translateY(0)',
            pointerEvents: 'auto',
          },
        }}
      >
        <DrawerTasksToolbar deal={deal} onUpdate={updateOnChange} checklistType={checklistType} isPM={IS_PM} />

        {/* Hover menu (appears over the header on hover) */}
        {currentChecklist && IS_PM && (
          <NcoHoverMenu
            addRow={handleAddRow}
            setConfirmOpen={setConfirmOpen}
            isLoading={creatingChecklist || addingTasks}
            isDisabled={!currentChecklist || !IS_PM}
            isDeleting={deletingChecklist}
          />
        )}
      </Box>

      {/* After creation, show a simple table to allow removals */}
      {seededRows.length > 0 ? (
        <TableContainer component={Paper}>
          <BoxScroller
            sx={{
              // Use the calculated maxHeight which accounts for header, toolbar (when pinned), and gutters
              maxHeight,
              overflowY: 'auto',
              transition: 'max-height 200ms ease',
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, px: 2 }}
              onMouseEnter={() => setIsHovered(true)}
            >
              <Box sx={{ flex: 1 }}>
                <LinearProgress variant="determinate" value={progress.pct} />
              </Box>
              <Typography variant="body2">{progress.pct}%</Typography>
            </Box>
            <Table
              size="small"
              stickyHeader
              sx={{
                '& .MuiTableCell-root': {
                  textAlign: 'left',
                },
              }}
              className="nco-table"
            >
              <TableHead>
                <TableRow>
                  <TableCell width={64}>#</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Dependencies</TableCell>
                  <TableCell width={140}>Role</TableCell>
                  <TableCell width={220}>Assignee</TableCell>
                  <TableCell width={160}>Status</TableCell>
                  <TableCell width={72} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* existing seeded rows */}
                {seededRows.map((row: any) => {
                  const assigneesForRow = teamForRow(row) ?? []
                  const savedIds = Array.isArray(row.assigneeIds) ? row.assigneeIds : []
                  const defaultIds = idsByRole.get(canonRole(row.role)) ?? []
                  const displayIds = savedIds.length ? Array.from(new Set([...savedIds, ...defaultIds])) : defaultIds

                  return (
                    <TableRow key={`${row.sectionIndex}_${row.itemId}`} hover sx={{ opacity: row?.canEdit ? 1 : 0.5 }}>
                      <SeededRows
                        row={{ ...row, assigneeIds: displayIds }}
                        options={assigneesForRow}
                        updateStatus={updateStatus}
                        updateAssignees={updateAssignees}
                        onRemoveSeeded={handleRemoveSeeded}
                      />
                    </TableRow>
                  )
                })}

                {/* DRAFT rows (editable) */}
                {draftRows.map(draft => (
                  <TableRow key={`${draft.sectionIndex}_${draft.itemId}`}>
                    <DraftRows
                      currentTeam={currentTeam}
                      draft={draft}
                      patchDraftRow={patchDraftRow}
                      saveDraftRow={saveDraftRow}
                      discardDraftRow={discardDraftRow}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </BoxScroller>
        </TableContainer>
      ) : (
        <Grid container sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', py: 2 }}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12} pl={2}>
            <IconButton onClick={handleStartClick} disabled={creatingChecklist || addingTasks}>
              <Tooltip title={<Typography>Select task list and start onboarding</Typography>}>
                {!creatingChecklist && !addingTasks ? (
                  <PlayCircleOutlineIcon
                    sx={{
                      height: 55,
                      width: 55,
                      color:
                        creatingChecklist || addingTasks || !IS_OWNER || !IS_PM
                          ? 'text.disabled'
                          : 'rgb(110, 193, 232)',
                    }}
                  />
                ) : (
                  <CircularProgress />
                )}
              </Tooltip>
            </IconButton>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12} pl={2}>
            <Typography variant="body2" color="text.warning">
              No checklist created yet. Please use the start button to create the onboarding checklist.
            </Typography>
          </Grid>
        </Grid>
      )}
      <OnboardingTemplateSelector
        deal={deal}
        pickerOpen={pickerOpen}
        setPickerOpen={setPickerOpen}
        selectedTemplateId={selectedTemplateId}
        setSelectedTemplateId={setSelectedTemplateId}
        selectedTemplate={selectedTemplate as any}
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        onConfirmTask={handleConfirmSeed}
      />
      <DeleteConfirmationDialog
        confirmOpen={confirmOpen}
        setConfirmOpen={setConfirmOpen}
        onDeleteEntireList={handleDeleteEntireList}
        isDeleting={deletingChecklist}
      />
    </Box>
  )
}

export default React.memo(OnboardingTasks)
